import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import PageSEO from '../components/PageSEO';
import {
  BookOpen, BarChart3, ChevronRight, Lightbulb, Microscope, Gamepad2, BookMarked,
  CheckCircle, XCircle, ArrowRight, Award, HelpCircle, Database, Eye, Wand2,
  AlertTriangle, Sparkles, SlidersHorizontal, Zap, Heart, Shield, Clock, DollarSign,
  Users, Activity, TrendingUp, ThumbsUp, ThumbsDown, Star, Trophy, Flame, Target,
  MapPin, Globe, Info, Layers, RotateCcw, ChevronDown, Search, GraduationCap, 
  FlaskConical, FileText, Play, Pause, RefreshCw, Brain, MessageCircle, Building,
  ExternalLink, Library, Book, Briefcase, BarChart2, ShieldCheck,
  HeartPulse, FlaskRound, Baby, MessageSquare
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell,
} from 'recharts';
import { ShareButtons } from '../components/ShareButtons';
import { SITE_MEDIA } from '../lib/siteMedia';

const API = process.env.REACT_APP_BACKEND_URL;
import { fetchWithFallback } from '../lib/dataFetcher';

// ============ ANIMATED COUNTER COMPONENT ============
const AnimatedCounter = ({ end, duration = 2, suffix = '', prefix = '', decimals = 0 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  useEffect(() => {
    if (!isInView) return;
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(easeOut * end);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isInView, end, duration]);

  return (
    <span ref={ref}>
      {prefix}{decimals > 0 ? count.toFixed(decimals) : Math.floor(count)}{suffix}
    </span>
  );
};

// ============ VISUAL STAT CARD WITH ANIMATION ============
const AnimatedStatCard = ({ stat, index }) => {
  const icons = { heart: Heart, shield: Shield, dollar: DollarSign, clock: Clock, users: Users, activity: Activity };
  const Icon = icons[stat.icon] || Heart;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.9 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.5, type: 'spring' }}
      whileHover={{ y: -8, boxShadow: '0 20px 40px -10px rgba(0,0,0,0.15)' }}
      className="relative overflow-hidden rounded-2xl bg-white border border-slate-200/60 p-5 group"
    >
      <div className="absolute top-0 right-0 w-24 h-24 -mr-8 -mt-8 rounded-full opacity-10 group-hover:opacity-20 transition-opacity" style={{ backgroundColor: stat.color }} />
      <motion.div 
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
        style={{ backgroundColor: `${stat.color}20` }}
        whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
        transition={{ duration: 0.5 }}
      >
        <Icon className="w-6 h-6" style={{ color: stat.color }} />
      </motion.div>
      <div className="text-3xl font-black text-slate-900 mb-1">
        <AnimatedCounter end={stat.value} suffix={stat.suffix} prefix={stat.prefix || ''} decimals={stat.value % 1 !== 0 ? 1 : 0} />
      </div>
      <p className="text-sm text-slate-600 leading-snug">{stat.label}</p>
    </motion.div>
  );
};

// ============ FLASHCARD SYSTEM ============
const FlashcardDeck = ({ deckId, onBack }) => {
  const [cards, setCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [progress, setProgress] = useState({ known: [], learning: [] });
  const [deckInfo, setDeckInfo] = useState(null);

  useEffect(() => {
    fetchWithFallback(`/api/education/flashcards?deck_id=${deckId}`)
      
      .then(d => {
        setCards(d.cards || []);
        setDeckInfo({ name: d.name, description: d.description, difficulty: d.difficulty });
      })
      .catch(() => {});
  }, [deckId]);

  const currentCard = cards[currentIndex];
  const totalKnown = progress.known.length;
  const totalLearning = progress.learning.length;

  const handleKnow = () => {
    if (currentCard && !progress.known.includes(currentCard.id)) {
      setProgress(p => ({ ...p, known: [...p.known, currentCard.id] }));
    }
    nextCard();
  };

  const handleStillLearning = () => {
    if (currentCard && !progress.learning.includes(currentCard.id)) {
      setProgress(p => ({ ...p, learning: [...p.learning, currentCard.id] }));
    }
    nextCard();
  };

  const nextCard = () => {
    setFlipped(false);
    setTimeout(() => {
      if (currentIndex + 1 < cards.length) {
        setCurrentIndex(currentIndex + 1);
      } else {
        setCurrentIndex(0);
      }
    }, 200);
  };

  const resetDeck = () => {
    setCurrentIndex(0);
    setProgress({ known: [], learning: [] });
    setFlipped(false);
  };

  if (!cards.length) return <div className="text-center py-12 text-slate-400">Loading flashcards...</div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-600 hover:text-violet-600">
          <ChevronRight className="w-4 h-4 rotate-180" /> Back to decks
        </button>
        <div className="flex items-center gap-3">
          <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">✓ {totalKnown}</span>
          <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700">📚 {totalLearning}</span>
          <button onClick={resetDeck} className="text-xs text-slate-500 hover:text-violet-600 flex items-center gap-1">
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-violet-500 to-purple-500 transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / cards.length) * 100}%` }}
        />
      </div>
      <p className="text-xs text-slate-400 text-center">{currentIndex + 1} / {cards.length}</p>

      {/* Flashcard */}
      <div 
        onClick={() => setFlipped(!flipped)}
        className="cursor-pointer perspective-1000 h-64 sm:h-72"
      >
        <motion.div
          animate={{ rotateY: flipped ? 180 : 0 }}
          transition={{ duration: 0.5, type: 'spring', stiffness: 100 }}
          className="relative w-full h-full preserve-3d"
        >
          {/* Front */}
          <div className="absolute inset-0 backface-hidden rounded-2xl bg-gradient-to-br from-violet-600 to-purple-700 p-6 flex flex-col items-center justify-center text-center shadow-lg">
            <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-white/20 text-[10px] text-white/80 uppercase tracking-wider">
              {currentCard?.category}
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">{currentCard?.front}</h3>
            <p className="text-sm text-violet-200">Tap to reveal definition</p>
          </div>
          {/* Back */}
          <div className="absolute inset-0 backface-hidden rotate-y-180 rounded-2xl bg-white border-2 border-violet-200 p-6 flex flex-col justify-center overflow-y-auto">
            <p className="text-sm text-slate-700 leading-relaxed mb-4">{currentCard?.back}</p>
            {currentCard?.example && (
              <div className="mt-auto p-3 rounded-xl bg-violet-50 border border-violet-200">
                <p className="text-xs text-violet-600 font-semibold mb-1">Example:</p>
                <p className="text-xs text-slate-600">{currentCard.example}</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleStillLearning}
          className="flex-1 py-3 rounded-xl bg-amber-50 border-2 border-amber-200 text-amber-700 font-semibold hover:bg-amber-100 transition-colors"
        >
          📚 Still Learning
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleKnow}
          className="flex-1 py-3 rounded-xl bg-emerald-50 border-2 border-emerald-200 text-emerald-700 font-semibold hover:bg-emerald-100 transition-colors"
        >
          ✓ Got It!
        </motion.button>
      </div>
    </div>
  );
};

const FlashcardsSection = () => {
  const [decks, setDecks] = useState([]);
  const [selectedDeck, setSelectedDeck] = useState(null);

  useEffect(() => {
    fetchWithFallback(`/api/education/flashcards`)
      
      .then(d => setDecks(d.decks || []))
      .catch(() => {});
  }, []);

  const difficultyColors = {
    beginner: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    intermediate: 'bg-amber-100 text-amber-700 border-amber-200',
    advanced: 'bg-red-100 text-red-700 border-red-200'
  };

  if (selectedDeck) {
    return (
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6">
        <FlashcardDeck deckId={selectedDeck} onBack={() => setSelectedDeck(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-3">
        {decks.map((deck, i) => (
          <motion.button
            key={deck.deck_id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            whileHover={{ y: -4, boxShadow: '0 10px 30px -10px rgba(0,0,0,0.1)' }}
            onClick={() => setSelectedDeck(deck.deck_id)}
            className="text-left rounded-2xl bg-white border border-slate-200 p-5 transition-all group"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Layers className="w-5 h-5 text-violet-600" />
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold uppercase ${difficultyColors[deck.difficulty]}`}>
                {deck.difficulty}
              </span>
            </div>
            <h3 className="font-bold text-slate-900 mb-1">{deck.name}</h3>
            <p className="text-xs text-slate-500 mb-3 line-clamp-2">{deck.description}</p>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">{deck.card_count} cards</span>
              <span className="text-xs font-semibold text-violet-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                Study <ArrowRight className="w-3 h-3" />
              </span>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

// ============ GLOSSARY COMPONENT ============
const GlossarySection = () => {
  const [terms, setTerms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTerm, setExpandedTerm] = useState(null);

  useEffect(() => {
    fetchWithFallback(`/api/education/glossary`)
      
      .then(d => {
        setTerms(d.terms || []);
        setCategories(d.categories || []);
      })
      .catch(() => {});
  }, []);

  const filteredTerms = terms.filter(t => {
    const matchesCategory = !selectedCategory || t.category === selectedCategory;
    const matchesSearch = !searchQuery || 
      t.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.definition.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const categoryIcons = {
    measures: BarChart3,
    bias: AlertTriangle,
    statistics: TrendingUp,
    'study design': FlaskConical,
    'infectious disease': Shield,
    screening: Search,
    clinical: Heart
  };

  return (
    <div className="space-y-4">
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search terms..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
              !selectedCategory ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            All
          </button>
          {categories.slice(0, 4).map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors capitalize ${
                selectedCategory === cat ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Terms Grid */}
      <div className="grid gap-3 sm:grid-cols-2">
        {filteredTerms.map((term, i) => {
          const Icon = categoryIcons[term.category] || BookOpen;
          const isExpanded = expandedTerm === term.term;
          
          return (
            <motion.div
              key={term.term}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.02 }}
              className="rounded-xl bg-white border border-slate-200 overflow-hidden"
            >
              <button
                onClick={() => setExpandedTerm(isExpanded ? null : term.term)}
                className="w-full text-left p-4 flex items-start gap-3 hover:bg-slate-50 transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center shrink-0 mt-0.5">
                  <Icon className="w-4 h-4 text-violet-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-slate-900">{term.term}</h4>
                  <p className={`text-sm text-slate-600 mt-1 ${isExpanded ? '' : 'line-clamp-2'}`}>
                    {term.definition}
                  </p>
                </div>
                <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </button>
              
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-slate-100 px-4 pb-4"
                  >
                    <div className="pt-3">
                      <p className="text-xs text-slate-500 mb-2">Related terms:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {term.related?.map(r => (
                          <span key={r} className="px-2 py-1 rounded-md bg-slate-100 text-xs text-slate-600">{r}</span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
      
      {filteredTerms.length === 0 && (
        <div className="text-center py-8 text-slate-400">
          No terms found matching "{searchQuery}"
        </div>
      )}
    </div>
  );
};

// ============ LANDMARK STUDIES SECTION ============
const LandmarkStudiesSection = () => {
  const [studies, setStudies] = useState([]);

  useEffect(() => {
    fetchWithFallback(`/api/education/landmark-studies`)
      
      .then(d => setStudies(d.studies || []))
      .catch(() => {});
  }, []);

  if (!studies.length) return null;

  return (
    <section id="landmark-studies" className="scroll-mt-20" data-testid="landmark-studies-section">
      <motion.div 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="text-center mb-6"
      >
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 text-sm font-semibold mb-3">
          <FlaskConical className="w-4 h-4" /> Public Health Research
        </div>
        <h2 className="text-2xl font-bold text-slate-900">Landmark Studies That Changed Public Health</h2>
        <p className="text-slate-500 mt-1">The research that shaped modern epidemiology</p>
      </motion.div>
      
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {studies.map((study, i) => (
          <motion.div
            key={study.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="rounded-2xl bg-white border border-slate-200 overflow-hidden hover:shadow-lg transition-all group"
          >
            <div className="h-32 overflow-hidden relative">
              <img src={study.image} alt={study.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
              <div className="absolute bottom-3 left-3 right-3">
                <span className="text-xs px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-sm text-white">{study.year}</span>
              </div>
            </div>
            <div className="p-4">
              <h3 className="font-bold text-slate-900 mb-1 line-clamp-1">{study.title}</h3>
              <p className="text-xs text-purple-600 font-medium mb-2">{study.institution}</p>
              <p className="text-sm text-slate-600 line-clamp-2 mb-3">{study.description}</p>
              <div className="flex gap-2">
                <a 
                  href={study.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex-1 px-3 py-2 rounded-lg bg-purple-50 text-purple-700 text-xs font-semibold text-center hover:bg-purple-100 transition-colors flex items-center justify-center gap-1"
                >
                  Learn More <ExternalLink className="w-3 h-3" />
                </a>
                <a 
                  href={study.pubmed_link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="px-3 py-2 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold hover:bg-slate-200 transition-colors flex items-center gap-1"
                >
                  PubMed <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

// ============ RECOMMENDED BOOKS SECTION ============

// ============ CAREER PATHS (for students exploring public health) ============
const CAREER_ICON_MAP = {
  microscope: Microscope,
  'bar-chart': BarChart2,
  'trending-up': TrendingUp,
  shield: ShieldCheck,
  users: Users,
  database: Database,
  globe: Globe,
  'heart-pulse': HeartPulse,
  search: Search,
  world: Globe,
  flask: FlaskRound,
  message: MessageSquare,
  baby: Baby,
};

// Career card gradient — hex-coded so Tailwind purge can't drop unused classes.
const CAREER_GRADIENT = {
  epidemiologist: 'linear-gradient(to right, #8b5cf6, #9333ea)',
  biostatistician: 'linear-gradient(to right, #3b82f6, #0891b2)',
  public_health_analyst: 'linear-gradient(to right, #10b981, #0d9488)',
  infection_preventionist: 'linear-gradient(to right, #f43f5e, #db2777)',
  public_health_advisor: 'linear-gradient(to right, #f59e0b, #ea580c)',
  health_data_scientist: 'linear-gradient(to right, #6366f1, #2563eb)',
  environmental_health_specialist: 'linear-gradient(to right, #84cc16, #16a34a)',
  public_health_nurse: 'linear-gradient(to right, #ec4899, #e11d48)',
  disease_intervention_specialist: 'linear-gradient(to right, #f97316, #dc2626)',
  global_health_specialist: 'linear-gradient(to right, #06b6d4, #0284c7)',
  public_health_lab_scientist: 'linear-gradient(to right, #14b8a6, #059669)',
  health_educator: 'linear-gradient(to right, #d946ef, #9333ea)',
  maternal_child_health_specialist: 'linear-gradient(to right, #f43f5e, #c026d3)',
};

const CareerPathsSection = () => {
  const [careers, setCareers] = useState([]);
  const [note, setNote] = useState('');
  const [sources, setSources] = useState([]);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    fetchWithFallback('/api/education/career-paths')
      .then((d) => {
        setCareers(d.careers || []);
        setNote(d.note || '');
        setSources(d.sources || []);
      })
      .catch(() => {});
  }, []);

  if (!careers.length) return null;

  return (
    <section id="career-paths" className="scroll-mt-20" data-testid="career-paths-section">
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="text-center mb-6"
      >
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 text-sm font-semibold mb-3">
          <Briefcase className="w-4 h-4" /> For Students
        </div>
        <h2 className="text-2xl font-bold text-slate-900">Public Health Career Paths</h2>
        <p className="text-slate-500 mt-1 max-w-2xl mx-auto">
          Explore what each role earns, what degree it needs, what the work looks like day-to-day, and the skills that will get you there.
        </p>
      </motion.div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {careers.map((c, i) => {
          const Icon = CAREER_ICON_MAP[c.icon] || GraduationCap;
          const isOpen = expanded === c.id;
          return (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="rounded-2xl bg-white border border-slate-200 overflow-hidden hover:shadow-lg transition-all flex flex-col"
              data-testid={`career-card-${c.id}`}
            >
              <div
                className="p-4"
                style={{ background: CAREER_GRADIENT[c.id] || 'linear-gradient(to right, #8b5cf6, #9333ea)' }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-bold text-white text-base leading-tight">{c.title}</h3>
                </div>
              </div>

              <div className="p-4 space-y-3 flex-1 flex flex-col">
                <p className="text-sm text-slate-700 leading-relaxed">{c.tagline}</p>

                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-2.5">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-700 uppercase tracking-wide mb-0.5">
                      <DollarSign className="w-3 h-3" /> Salary
                    </div>
                    <div className="text-xs font-semibold text-slate-900 leading-tight">{c.salary_range}</div>
                  </div>
                  <div className="rounded-lg bg-blue-50 border border-blue-100 p-2.5">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-blue-700 uppercase tracking-wide mb-0.5">
                      <TrendingUp className="w-3 h-3" /> Outlook
                    </div>
                    <div className="text-xs font-semibold text-slate-900 leading-tight line-clamp-2">{c.growth_outlook}</div>
                  </div>
                </div>

                <button
                  onClick={() => setExpanded(isOpen ? null : c.id)}
                  className="w-full text-xs font-semibold text-violet-600 hover:text-violet-700 flex items-center justify-between mt-auto"
                  data-testid={`career-toggle-${c.id}`}
                >
                  {isOpen ? 'Hide details' : 'Degree · daily work · skills'}
                  <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-3 overflow-hidden"
                    >
                      {c.typical_degrees && (
                        <div>
                          <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-700 uppercase tracking-wide mb-1">
                            <GraduationCap className="w-3 h-3" /> Typical degree
                          </div>
                          <ul className="space-y-1">
                            {c.typical_degrees.map((d, j) => (
                              <li key={j} className="text-xs text-slate-600 leading-snug">• {d}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {c.daily_work && (
                        <div>
                          <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-700 uppercase tracking-wide mb-1">
                            <Clock className="w-3 h-3" /> Daily work
                          </div>
                          <ul className="space-y-1">
                            {c.daily_work.map((d, j) => (
                              <li key={j} className="text-xs text-slate-600 leading-snug">• {d}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {c.skills_needed && (
                        <div>
                          <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-700 uppercase tracking-wide mb-1">
                            <Brain className="w-3 h-3" /> Skills needed
                          </div>
                          <ul className="space-y-1">
                            {c.skills_needed.map((s, j) => (
                              <li key={j} className="text-xs text-slate-600 leading-snug">• {s}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {c.where_they_work && (
                        <div>
                          <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-700 uppercase tracking-wide mb-1">
                            <Building className="w-3 h-3" /> Where they work
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {c.where_they_work.map((w, j) => (
                              <span key={j} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{w}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          );
        })}
      </div>

      {(note || sources.length > 0) && (
        <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600 space-y-2">
          {note && <p>{note}</p>}
          {sources.length > 0 && (
            <div className="flex flex-wrap gap-3">
              <span className="font-semibold text-slate-700">Sources:</span>
              {sources.map((s, i) => (
                <a
                  key={i}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-violet-600 hover:underline"
                >
                  {s.name} <ExternalLink className="w-3 h-3" />
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

const RecommendedBooksSection = () => {
  const [books, setBooks] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchWithFallback(`/api/education/recommended-books`)
      
      .then(d => {
        setBooks(d.books || []);
        setCategories(d.categories || []);
      })
      .catch(() => {});
  }, []);

  const filteredBooks = selectedCategory 
    ? books.filter(b => b.category === selectedCategory) 
    : books;

  const categoryLabels = {
    history: 'History',
    ethics: 'Ethics',
    infectious_disease: 'Infectious Disease',
    pandemic: 'Pandemics',
    chronic_disease: 'Chronic Disease',
    vaccines: 'Vaccines',
    healthcare: 'Healthcare',
    memoir: 'Memoir',
    global_health: 'Global Health'
  };

  if (!books.length) return null;

  return (
    <section id="recommended-books" className="scroll-mt-20" data-testid="recommended-books-section">
      <motion.div 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="text-center mb-6"
      >
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 text-sm font-semibold mb-3">
          <Book className="w-4 h-4" /> Reading List
        </div>
        <h2 className="text-2xl font-bold text-slate-900">Recommended Books</h2>
        <p className="text-slate-500 mt-1">Essential reading for understanding public health</p>
      </motion.div>

      {/* Category Filter */}
      <div className="flex gap-2 flex-wrap justify-center mb-6">
        <button
          onClick={() => setSelectedCategory(null)}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
            !selectedCategory ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          All Books
        </button>
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              selectedCategory === cat ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {categoryLabels[cat] || cat}
          </button>
        ))}
      </div>
      
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {filteredBooks.map((book, i) => (
          <motion.a
            key={book.id}
            href={book.amazon_link}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.05 }}
            className="rounded-2xl bg-white border border-slate-200 overflow-hidden hover:shadow-lg transition-all group flex flex-col"
          >
            <div className="h-48 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
              {book.image ? (
                <img
                  src={book.image}
                  alt={book.title}
                  referrerPolicy="no-referrer"
                  crossOrigin="anonymous"
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                  data-testid={`book-cover-${book.id}`}
                />
              ) : null}
              <div className="items-center justify-center" style={{ display: book.image ? 'none' : 'flex' }}>
                <Book className="w-12 h-12 text-slate-300" />
              </div>
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold self-start mb-2">
                {categoryLabels[book.category] || book.category}
              </span>
              <h3 className="font-bold text-slate-900 text-sm leading-tight line-clamp-2 mb-1">{book.title}</h3>
              <p className="text-xs text-slate-500 mb-2">by {book.author} ({book.year})</p>
              <p className="text-xs text-slate-600 line-clamp-3 flex-1">{book.description}</p>
              <div className="mt-3 pt-3 border-t border-slate-100">
                <span className="text-xs font-semibold text-amber-600 flex items-center gap-1">
                  View on Amazon <ExternalLink className="w-3 h-3" />
                </span>
              </div>
            </div>
          </motion.a>
        ))}
      </div>
    </section>
  );
};

// ============ CASE STUDIES (REAL IMPACT) ============
const CaseStudiesSection = () => {
  const [studies, setStudies] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [fullStudy, setFullStudy] = useState(null);

  useEffect(() => {
    fetchWithFallback(`/api/education/case-studies`)
      
      .then(d => setStudies(d.case_studies || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedStudy) {
      fetchWithFallback(`/api/education/case-studies?study_id=${selectedStudy}`)
        
        .then(d => setFullStudy(d))
        .catch(() => {});
    }
  }, [selectedStudy]);

  if (selectedStudy && fullStudy) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="space-y-6"
      >
        <button onClick={() => { setSelectedStudy(null); setFullStudy(null); }} className="flex items-center gap-2 text-sm text-slate-600 hover:text-violet-600">
          <ChevronRight className="w-4 h-4 rotate-180" /> Back to case studies
        </button>

        {/* Study Header */}
        <div className="rounded-2xl overflow-hidden">
          {fullStudy.image && (
            <div className="h-48 overflow-hidden">
              <img src={fullStudy.image} alt={fullStudy.title} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="bg-gradient-to-br from-violet-600 to-purple-700 p-6 text-white">
            <span className="text-xs px-2 py-1 rounded-full bg-white/20 uppercase tracking-wider">{fullStudy.category}</span>
            <h2 className="text-2xl font-bold mt-3 mb-2">{fullStudy.title}</h2>
            <p className="text-violet-200">{fullStudy.summary}</p>
          </div>
        </div>

        {/* Timeline */}
        {fullStudy.timeline && (
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-violet-600" /> Timeline
            </h3>
            <div className="space-y-4">
              {fullStudy.timeline.map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-20 shrink-0">
                    <span className="text-xs font-bold text-violet-600 bg-violet-50 px-2 py-1 rounded">{item.date}</span>
                  </div>
                  <div className="flex-1 pb-4 border-l-2 border-violet-200 pl-4">
                    <p className="font-semibold text-slate-900 text-sm">{item.event}</p>
                    <p className="text-xs text-slate-500 mt-1">{item.epi_action}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Data Points - Before/After */}
        {fullStudy.data_points && (
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-violet-600" /> Impact Data
            </h3>
            <div className="grid gap-4 sm:grid-cols-3">
              {fullStudy.data_points.map((dp, i) => (
                <div key={i} className="rounded-xl bg-slate-50 p-4">
                  <p className="text-xs text-slate-500 mb-2">{dp.metric}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-400 line-through">{dp.before}</span>
                    <ArrowRight className="w-4 h-4 text-violet-400" />
                    <span className="text-lg font-bold text-violet-700">{dp.after}</span>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-2">Source: {dp.source}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Key Lesson */}
        {fullStudy.key_lesson && (
          <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 p-6">
            <h3 className="font-bold text-emerald-800 mb-2 flex items-center gap-2">
              <Lightbulb className="w-5 h-5" /> Key Lesson
            </h3>
            <p className="text-sm text-emerald-700 leading-relaxed">{fullStudy.key_lesson}</p>
          </div>
        )}
      </motion.div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {studies.map((study, i) => (
        <motion.button
          key={study.id}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.1 }}
          whileHover={{ y: -4 }}
          onClick={() => setSelectedStudy(study.id)}
          className="text-left rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-sm hover:shadow-lg transition-all group"
        >
          {study.image && (
            <div className="h-36 overflow-hidden">
              <img src={study.image} alt={study.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
          )}
          <div className="p-5">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 uppercase tracking-wider font-semibold">
              {study.category}
            </span>
            <h3 className="font-bold text-slate-900 mt-2 mb-1">{study.title}</h3>
            <p className="text-sm text-slate-600 line-clamp-2">{study.summary}</p>
            <span className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-violet-600">
              Read case study <ArrowRight className="w-3 h-3" />
            </span>
          </div>
        </motion.button>
      ))}
    </div>
  );
};

// ============ BUILD YOUR OWN STUDY ============
const BuildYourOwnStudy = () => {
  const [formData, setFormData] = useState({
    population: 'General US Adults 45-65',
    exposure: 'Daily Exercise (≥30 min)',
    outcome: 'Type 2 Diabetes',
    follow_up_years: 10,
    sample_size: 10000
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const exposureOptions = [
    'Daily Exercise (≥30 min)',
    'Current Smoking',
    'Mediterranean Diet',
    'Obesity (BMI ≥30)',
    'Heavy Alcohol Use'
  ];

  const outcomeOptions = [
    'Type 2 Diabetes',
    'Heart Disease',
    'Lung Cancer',
    'Stroke',
    'Hypertension'
  ];

  const runSimulation = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        population: formData.population,
        exposure: formData.exposure,
        outcome: formData.outcome,
        follow_up_years: formData.follow_up_years,
        sample_size: formData.sample_size
      });
      const res = await fetchWithFallback(`/api/education/build-study?${params}`, { method: 'POST' });
      const data = await res.json();
      setResults(data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Form */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">Population</label>
          <input
            type="text"
            value={formData.population}
            onChange={e => setFormData({ ...formData, population: e.target.value })}
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-violet-400"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">Exposure</label>
          <select
            value={formData.exposure}
            onChange={e => setFormData({ ...formData, exposure: e.target.value })}
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-violet-400 bg-white"
          >
            {exposureOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">Outcome</label>
          <select
            value={formData.outcome}
            onChange={e => setFormData({ ...formData, outcome: e.target.value })}
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-violet-400 bg-white"
          >
            {outcomeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">Follow-up (years)</label>
          <input
            type="number"
            min={1}
            max={30}
            value={formData.follow_up_years}
            onChange={e => setFormData({ ...formData, follow_up_years: parseInt(e.target.value) || 10 })}
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-violet-400"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">Sample Size</label>
          <input
            type="number"
            min={100}
            max={100000}
            step={100}
            value={formData.sample_size}
            onChange={e => setFormData({ ...formData, sample_size: parseInt(e.target.value) || 10000 })}
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-violet-400"
          />
        </div>
        <div className="flex items-end">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={runSimulation}
            disabled={loading}
            className="w-full py-2.5 rounded-xl bg-violet-600 text-white font-semibold hover:bg-violet-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {loading ? 'Running...' : 'Run Simulation'}
          </motion.button>
        </div>
      </div>

      {/* Results */}
      {results && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Cohort Diagram */}
          <div className="rounded-2xl bg-slate-900 p-6 text-white">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-violet-400" /> Cohort Study Diagram
            </h3>
            <div className="flex flex-col items-center gap-4">
              {/* Source */}
              <div className="px-4 py-2 rounded-lg bg-violet-600 text-center">
                <p className="text-xs text-violet-200">Source Population</p>
                <p className="font-bold">{results.cohort_diagram?.source_population}</p>
              </div>
              <div className="w-0.5 h-6 bg-violet-500" />
              {/* Enrolled */}
              <div className="px-4 py-2 rounded-lg bg-violet-500/50 text-center">
                <p className="text-xs text-violet-200">Enrolled</p>
                <p className="font-bold">{results.cohort_diagram?.enrolled?.toLocaleString()}</p>
              </div>
              <div className="w-0.5 h-6 bg-violet-400" />
              {/* Split */}
              <div className="flex gap-8">
                <div className="flex flex-col items-center">
                  <div className="px-4 py-3 rounded-lg bg-emerald-600 text-center">
                    <p className="text-xs text-emerald-200">Exposed</p>
                    <p className="font-bold">{results.cohort_diagram?.exposed_group?.n?.toLocaleString()}</p>
                  </div>
                  <div className="w-0.5 h-6 bg-emerald-400" />
                  <div className="flex gap-2 text-xs">
                    <span className="px-2 py-1 rounded bg-red-500/80">Cases: {results.cohort_diagram?.exposed_outcomes?.cases}</span>
                    <span className="px-2 py-1 rounded bg-slate-600">Non-cases: {results.cohort_diagram?.exposed_outcomes?.non_cases}</span>
                  </div>
                </div>
                <div className="flex flex-col items-center">
                  <div className="px-4 py-3 rounded-lg bg-slate-600 text-center">
                    <p className="text-xs text-slate-300">Unexposed</p>
                    <p className="font-bold">{results.cohort_diagram?.unexposed_group?.n?.toLocaleString()}</p>
                  </div>
                  <div className="w-0.5 h-6 bg-slate-400" />
                  <div className="flex gap-2 text-xs">
                    <span className="px-2 py-1 rounded bg-red-500/80">Cases: {results.cohort_diagram?.unexposed_outcomes?.cases}</span>
                    <span className="px-2 py-1 rounded bg-slate-600">Non-cases: {results.cohort_diagram?.unexposed_outcomes?.non_cases}</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">Follow-up: {results.cohort_diagram?.follow_up}</p>
            </div>
          </div>

          {/* Results Summary */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl bg-white border border-slate-200 p-4 text-center">
              <p className="text-xs text-slate-500 mb-1">Incidence (Exposed)</p>
              <p className="text-2xl font-bold text-violet-600">{results.results?.incidence_exposed}</p>
            </div>
            <div className="rounded-xl bg-white border border-slate-200 p-4 text-center">
              <p className="text-xs text-slate-500 mb-1">Incidence (Unexposed)</p>
              <p className="text-2xl font-bold text-slate-600">{results.results?.incidence_unexposed}</p>
            </div>
            <div className="rounded-xl bg-white border border-emerald-200 bg-emerald-50 p-4 text-center">
              <p className="text-xs text-emerald-600 mb-1">Relative Risk</p>
              <p className="text-2xl font-bold text-emerald-700">{results.results?.relative_risk}</p>
            </div>
            <div className="rounded-xl bg-white border border-slate-200 p-4 text-center">
              <p className="text-xs text-slate-500 mb-1">NNT</p>
              <p className="text-2xl font-bold text-slate-700">{results.results?.number_needed_to_treat || 'N/A'}</p>
            </div>
          </div>

          {/* Interpretation */}
          <div className="rounded-xl bg-blue-50 border border-blue-200 p-4">
            <p className="text-sm text-blue-800 font-medium mb-2">📊 Interpretation:</p>
            <p className="text-sm text-blue-700">{results.results?.interpretation}</p>
          </div>

          {/* Limitations */}
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
            <p className="text-sm text-amber-800 font-medium mb-2">⚠️ Important Limitations:</p>
            <ul className="text-xs text-amber-700 space-y-1">
              {results.limitations?.map((lim, i) => (
                <li key={i} className="flex items-start gap-2">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  {lim}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      )}
    </div>
  );
};

// ============ A DAY IN THE LIFE ============
const EpidemiologistDay = () => {
  const [data, setData] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(0);

  useEffect(() => {
    fetchWithFallback(`/api/education/epidemiologist-day`)
      
      .then(d => setData(d))
      .catch(() => {});
  }, []);

  if (!data) return null;

  const profile = data.profiles?.[selectedProfile];

  return (
    <div className="space-y-6">
      {/* Profile Selector */}
      <div className="flex gap-4 overflow-x-auto pb-2">
        {data.profiles?.map((p, i) => (
          <button
            key={i}
            onClick={() => setSelectedProfile(i)}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all shrink-0 ${
              selectedProfile === i 
                ? 'bg-violet-50 border-violet-300' 
                : 'bg-white border-slate-200 hover:border-slate-300'
            }`}
          >
            <img src={p.image} alt={p.name} className="w-12 h-12 rounded-full object-cover" />
            <div className="text-left">
              <p className="font-semibold text-slate-900 text-sm">{p.name}</p>
              <p className="text-xs text-slate-500">{p.role}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Day Schedule */}
      {profile && (
        <motion.div
          key={selectedProfile}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-4"
        >
          <div className="rounded-2xl bg-gradient-to-br from-violet-600 to-purple-700 p-6 text-white">
            <div className="flex items-center gap-4">
              <img src={profile.image} alt={profile.name} className="w-16 h-16 rounded-xl object-cover border-2 border-white/30" />
              <div>
                <h3 className="text-xl font-bold">{profile.name}</h3>
                <p className="text-violet-200">{profile.role}</p>
                <p className="text-xs text-violet-300 mt-1">{profile.organization}</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 divide-y divide-slate-100">
            {profile.day?.map((item, i) => (
              <div key={i} className="flex gap-4 p-4">
                <div className="w-16 shrink-0">
                  <span className="text-xs font-bold text-violet-600">{item.time}</span>
                </div>
                <div>
                  <p className="font-semibold text-slate-900 text-sm">{item.activity}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>

          {profile.quote && (
            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5">
              <p className="text-sm text-slate-700 italic">"{profile.quote}"</p>
              <p className="text-xs text-slate-500 mt-2">— {profile.name}</p>
            </div>
          )}
        </motion.div>
      )}

      {/* Skills */}
      {data.skills_used && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {data.skills_used.map((skill, i) => {
            const icons = { 'bar-chart': BarChart3, 'message-circle': MessageCircle, search: Search, brain: Brain };
            const Icon = icons[skill.icon] || BookOpen;
            return (
              <div key={i} className="rounded-xl bg-white border border-slate-200 p-4">
                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center mb-2">
                  <Icon className="w-4 h-4 text-violet-600" />
                </div>
                <p className="font-semibold text-slate-900 text-sm">{skill.skill}</p>
                <p className="text-xs text-slate-500 mt-1">{skill.description}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============ MYTH BUSTER GAME ============
const MythBusterGame = ({ myths }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [gameComplete, setGameComplete] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  const currentMyth = myths[currentIndex];

  const handleAnswer = (userAnswer) => {
    if (answered) return;
    const correct = userAnswer === currentMyth.is_true;
    setIsCorrect(correct);
    setAnswered(true);
    setShowExplanation(true);
    
    if (correct) {
      const points = currentMyth.difficulty === 'hard' ? 30 : currentMyth.difficulty === 'medium' ? 20 : 10;
      const streakBonus = streak >= 3 ? Math.floor(points * 0.5) : 0;
      setScore(prev => prev + points + streakBonus);
      setStreak(prev => {
        const newStreak = prev + 1;
        if (newStreak > bestStreak) setBestStreak(newStreak);
        return newStreak;
      });
    } else {
      setStreak(0);
    }
  };

  const nextQuestion = () => {
    if (currentIndex + 1 >= myths.length) {
      setGameComplete(true);
    } else {
      setCurrentIndex(prev => prev + 1);
      setAnswered(false);
      setIsCorrect(null);
      setShowExplanation(false);
    }
  };

  const resetGame = () => {
    setCurrentIndex(0);
    setScore(0);
    setStreak(0);
    setAnswered(false);
    setIsCorrect(null);
    setGameComplete(false);
    setShowExplanation(false);
  };

  if (!currentMyth) return null;

  if (gameComplete) {
    const percentage = Math.round((score / (myths.length * 20)) * 100);
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
      >
        <motion.div
          animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 0.6 }}
          className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-4 shadow-lg"
        >
          <Trophy className="w-12 h-12 text-white" />
        </motion.div>
        <h3 className="text-2xl font-bold text-slate-900 mb-2">Game Complete!</h3>
        <p className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-pink-600 mb-2">{score} points</p>
        <p className="text-sm text-slate-500 mb-1">Best streak: {bestStreak} 🔥</p>
        <p className="text-sm text-slate-500 mb-6">{percentage >= 80 ? '🏆 Health Expert!' : percentage >= 60 ? '📚 Well Informed!' : '💪 Keep Learning!'}</p>
        <div className="flex gap-3 justify-center">
          <button onClick={resetGame} className="px-5 py-2.5 rounded-xl bg-violet-600 text-white font-semibold hover:bg-violet-700 transition-colors">
            Play Again
          </button>
          <ShareButtons title={`I scored ${score} points in the EpiSplain Myth Buster game!`} />
        </div>
      </motion.div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Progress & Score Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-100 text-violet-700">
            <Star className="w-4 h-4" />
            <span className="font-bold">{score}</span>
          </div>
          {streak >= 2 && (
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-orange-100 text-orange-700"
            >
              <Flame className="w-4 h-4" />
              <span className="font-bold">{streak}x</span>
            </motion.div>
          )}
        </div>
        <span className="text-sm text-slate-500">{currentIndex + 1} / {myths.length}</span>
      </div>

      {/* Progress Bar */}
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <motion.div 
          className="h-full bg-gradient-to-r from-violet-500 to-pink-500 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${((currentIndex) / myths.length) * 100}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>

      {/* Question Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          className="rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 p-6"
        >
          <div className="flex items-center gap-2 mb-3">
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
              currentMyth.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
              currentMyth.difficulty === 'medium' ? 'bg-amber-100 text-amber-700' :
              'bg-green-100 text-green-700'
            }`}>
              {currentMyth.difficulty}
            </span>
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
              {currentMyth.category}
            </span>
          </div>
          
          <p className="text-lg font-semibold text-slate-900 mb-6 leading-relaxed">
            "{currentMyth.statement}"
          </p>

          {!answered ? (
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleAnswer(true)}
                className="flex-1 flex items-center justify-center gap-2 py-4 rounded-xl bg-emerald-50 border-2 border-emerald-200 text-emerald-700 font-bold hover:bg-emerald-100 hover:border-emerald-300 transition-all"
              >
                <ThumbsUp className="w-5 h-5" /> TRUE
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleAnswer(false)}
                className="flex-1 flex items-center justify-center gap-2 py-4 rounded-xl bg-red-50 border-2 border-red-200 text-red-700 font-bold hover:bg-red-100 hover:border-red-300 transition-all"
              >
                <ThumbsDown className="w-5 h-5" /> FALSE
              </motion.button>
            </div>
          ) : (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div className={`rounded-xl p-4 ${isCorrect ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {isCorrect ? (
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className={`font-bold ${isCorrect ? 'text-emerald-700' : 'text-red-700'}`}>
                    {isCorrect ? 'Correct!' : 'Not quite!'}
                  </span>
                  {isCorrect && streak >= 3 && (
                    <span className="ml-auto text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">+Streak Bonus!</span>
                  )}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">{currentMyth.explanation}</p>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={nextQuestion}
                className="w-full py-3 rounded-xl bg-violet-600 text-white font-semibold hover:bg-violet-700 transition-colors flex items-center justify-center gap-2"
              >
                {currentIndex + 1 >= myths.length ? 'See Results' : 'Next Question'}
                <ArrowRight className="w-4 h-4" />
              </motion.button>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// ============ INTERACTIVE CONCEPT VISUALIZER (ENHANCED) ============
const ConceptVisualizer = () => {
  const [activeDemo, setActiveDemo] = useState('rates');
  const [rateView, setRateView] = useState('rate');
  const [missingMode, setMissingMode] = useState('full');
  const [selectedDataset, setSelectedDataset] = useState(0);
  const [interactiveData, setInteractiveData] = useState(null);
  const [trendMetric, setTrendMetric] = useState(0);

  useEffect(() => {
    fetchWithFallback(`/api/education/interactive-data`)
      
      .then(d => setInteractiveData(d))
      .catch(() => {});
  }, []);

  const colors = useMemo(() => ['#8b5cf6', '#06b6d4', '#f97316', '#10b981', '#ec4899'], []);
  
  const rateData = useMemo(() => {
    const apiData = interactiveData?.rate_vs_count?.datasets?.[selectedDataset]?.rate_data;
    if (apiData) {
      return apiData.map((d, i) => ({
        group: d.group,
        rate: d.value,
        count: Math.round((d.value / 100) * d.population),
        color: colors[i % colors.length]
      }));
    }
    return [
      { group: '18-34', rate: 4.2, count: 3024000, color: '#8b5cf6' },
      { group: '35-44', rate: 10.8, count: 4428000, color: '#06b6d4' },
      { group: '45-54', rate: 15.6, count: 6240000, color: '#f97316' },
      { group: '55-64', rate: 20.1, count: 8442000, color: '#10b981' },
      { group: '65+', rate: 27.2, count: 14688000, color: '#ec4899' },
    ];
  }, [interactiveData, selectedDataset, colors]);

  const missingScenarios = interactiveData?.missing_data_demo?.scenarios || [
    { name: 'Complete', groups: [{ group: 'High Income', value: 78 }, { group: 'Middle Income', value: 61 }, { group: 'Low Income', value: 48 }] },
    { name: '20% Missing', groups: [{ group: 'High Income', value: 76 }, { group: 'Middle Income', value: 59 }, { group: 'Low Income', value: 46 }] },
    { name: 'Biased', groups: [{ group: 'High Income', value: 78 }, { group: 'Middle Income', value: 61 }, { group: 'Low Income', value: 18 }] },
  ];
  const missingData = missingScenarios.find(s => s.name.includes(missingMode === 'full' ? 'Complete' : missingMode === 'missing20' ? '20%' : 'Biased'))?.groups?.map((g, i) => ({
    ...g, color: colors[i % colors.length]
  })) || [];

  const trendData = interactiveData?.trend_comparison?.datasets || [];

  return (
    <div className="space-y-4">
      {/* Demo Tabs */}
      <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
        {[
          { id: 'rates', label: 'Rate vs Count', icon: BarChart3 },
          { id: 'missing', label: 'Missing Data', icon: Database },
          { id: 'trends', label: 'Trends', icon: TrendingUp },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveDemo(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              activeDemo === tab.id ? 'bg-white text-violet-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {activeDemo === 'rates' && (
          <motion.div
            key="rates"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            {/* Dataset selector */}
            {interactiveData?.rate_vs_count?.datasets && (
              <div className="flex gap-2 flex-wrap">
                {interactiveData.rate_vs_count.datasets.map((ds, i) => (
                  <button key={i} onClick={() => setSelectedDataset(i)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      selectedDataset === i ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {ds.name}
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              {['rate', 'count'].map(view => (
                <button
                  key={view}
                  onClick={() => setRateView(view)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    rateView === view ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {view === 'rate' ? '📊 Rate (%)' : '🔢 Raw Count'}
                </button>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={rateData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="group" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <Tooltip formatter={(v) => rateView === 'rate' ? `${v}%` : v.toLocaleString()} />
                <Bar dataKey={rateView} radius={[8, 8, 0, 0]}>
                  {rateData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="p-3 rounded-xl bg-violet-50 border border-violet-200">
              <p className="text-xs text-violet-500 mb-1">Source: {interactiveData?.rate_vs_count?.datasets?.[selectedDataset]?.source || 'CDC BRFSS'}</p>
              <p className="text-sm text-violet-800">
                <strong>💡 Insight:</strong> {interactiveData?.rate_vs_count?.datasets?.[selectedDataset]?.insight || 'Rates allow fair comparison between groups of different sizes'}
              </p>
            </div>
          </motion.div>
        )}

        {activeDemo === 'missing' && (
          <motion.div
            key="missing"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'full', label: '✓ Complete data' },
                { id: 'missing20', label: '− Random 20% missing' },
                { id: 'missingGroup', label: '⚠ Systematic bias' },
              ].map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setMissingMode(opt.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    missingMode === opt.id ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={missingData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="group" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <Tooltip />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                  {missingData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
              <p className="text-xs text-amber-600 mb-1">Source: {interactiveData?.missing_data_demo?.source || 'Simulated'}</p>
              <p className="text-sm text-amber-800">
                <strong>⚠️ Watch out:</strong> {missingMode === 'full' 
                  ? 'Complete data shows the true pattern across income groups'
                  : missingMode === 'missing20' 
                  ? 'Random missing data reduces values proportionally — pattern is preserved'
                  : 'When one group is systematically undersampled, the chart tells a misleading story!'}
              </p>
            </div>
          </motion.div>
        )}

        {activeDemo === 'trends' && (
          <motion.div
            key="trends"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            {trendData.length > 0 && (
              <>
                <div className="flex gap-2 flex-wrap">
                  {trendData.map((ds, i) => (
                    <button key={i} onClick={() => setTrendMetric(i)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        trendMetric === i ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {ds.name.replace(' 2010-2024', '')}
                    </button>
                  ))}
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={trendData[trendMetric]?.data || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="year" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" domain={['auto', 'auto']} />
                    <Tooltip formatter={(v) => `${v}%`} />
                    <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={3} dot={{ r: 4, fill: '#10b981' }} />
                  </LineChart>
                </ResponsiveContainer>
                <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                  <p className="text-xs text-emerald-600 mb-1">Source: {trendData[trendMetric]?.source || 'CDC'}</p>
                  <p className="text-sm text-emerald-800">
                    <strong>📈 Trend:</strong> {trendData[trendMetric]?.name?.includes('Smoking') 
                      ? 'Smoking rates have dropped nearly in half since 2010 — a public health success story!'
                      : trendData[trendMetric]?.name?.includes('Obesity')
                      ? 'Obesity rates continue to climb, now affecting over 1 in 3 American adults'
                      : 'Diabetes rates have risen steadily, driven by obesity and aging population'}
                  </p>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============ VISUAL ANALOGY CARDS ============
const AnalogyCard = ({ analogy, index }) => {
  const [flipped, setFlipped] = useState(false);
  const icons = { filter: Eye, shuffle: Zap, shield: Shield, 'pie-chart': Activity, 'trending-up': TrendingUp, 'alert-triangle': AlertTriangle };
  const Icon = icons[analogy.icon] || Lightbulb;
  const gradients = ['from-violet-500 to-purple-600', 'from-cyan-500 to-blue-600', 'from-amber-500 to-orange-600', 'from-emerald-500 to-teal-600', 'from-pink-500 to-rose-600', 'from-indigo-500 to-violet-600'];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08 }}
      onClick={() => setFlipped(!flipped)}
      className="cursor-pointer perspective-1000 h-44"
    >
      <motion.div
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.6, type: 'spring' }}
        className="relative w-full h-full preserve-3d"
      >
        {/* Front */}
        <div className="absolute inset-0 backface-hidden rounded-2xl bg-white border border-slate-200 p-5 flex flex-col items-center justify-center text-center shadow-sm hover:shadow-md transition-shadow">
          <motion.div 
            className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${gradients[index % gradients.length]} flex items-center justify-center mb-3 shadow-md`}
            whileHover={{ scale: 1.1, rotate: 5 }}
          >
            <Icon className="w-7 h-7 text-white" />
          </motion.div>
          <h3 className="font-bold text-slate-900">{analogy.concept}</h3>
          <p className="text-xs text-slate-400 mt-1">Tap to see analogy</p>
        </div>
        {/* Back */}
        <div className="absolute inset-0 backface-hidden rotate-y-180 rounded-2xl bg-gradient-to-br from-violet-50 to-white border border-violet-200 p-5 flex flex-col justify-center">
          <p className="text-sm font-semibold text-violet-700 mb-2">Think of it like...</p>
          <p className="text-sm text-slate-700 leading-relaxed">{analogy.analogy}</p>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============ DATA COVERAGE INDICATOR ============
const DataCoverageIndicator = ({ coverage }) => {
  if (!coverage) return null;
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="rounded-2xl bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 p-5"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
          <Globe className="w-6 h-6 text-blue-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-slate-900 mb-1">CDC PLACES 2025 Data Coverage</h3>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Covered</p>
              <p className="text-sm font-semibold text-emerald-700">✓ 50 states + DC</p>
              <p className="text-xs text-slate-500">{coverage.covered?.total_population}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Not Covered</p>
              <p className="text-sm font-semibold text-amber-700">⚠ U.S. Territories</p>
              <p className="text-xs text-slate-500">~3.6M Americans</p>
            </div>
          </div>
          {coverage.not_covered?.territories && (
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-xs text-slate-500 mb-2">Territories not in CDC PLACES:</p>
              <div className="flex flex-wrap gap-1.5">
                {coverage.not_covered.territories.map(t => (
                  <span key={t.code} className="px-2 py-1 rounded-md bg-white text-xs text-slate-600 border border-slate-200">
                    {t.code}: {t.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ============ RESEARCH METHODS SECTION (merged from MethodsEducation) ============
const MethodContextBox = ({ icon: Icon, label, color, children }) => (
  <div className={`rounded-xl border p-4 ${color}`}>
    <div className="flex items-center gap-1.5 mb-2">
      <Icon className="w-4 h-4" />
      <p className="text-xs font-bold uppercase tracking-wider">{label}</p>
    </div>
    <p className="text-sm leading-relaxed">{children}</p>
  </div>
);

const METHOD_ICONS = {
  'propensity-scores': SlidersHorizontal, 'survival-analysis': Clock, 'hazard-ratios': Zap,
  'common-biases': AlertTriangle, 'missing-data': HelpCircle,
  'p-values-vs-effects': Target, 'study-designs': BookOpen, 'key-terms': BookMarked,
};

const MethodCard = ({ section, index }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = METHOD_ICONS[section.id] || Microscope;
  const colors = [
    'from-teal-500 to-emerald-500', 'from-blue-500 to-indigo-500', 'from-amber-500 to-orange-500',
    'from-red-500 to-rose-500', 'from-purple-500 to-violet-500', 'from-pink-500 to-rose-500',
    'from-emerald-500 to-teal-500', 'from-indigo-500 to-blue-500',
  ];
  const gradient = colors[index % colors.length];

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.06 }}
      className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden" data-testid={`method-${section.id}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full p-5 text-left flex items-start gap-3 hover:bg-slate-50/50 transition-colors">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shrink-0 shadow-sm`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-bold text-slate-900">{section.title}</h4>
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{section.what}</p>
        </div>
        <ChevronRight className={`w-4 h-4 text-slate-400 shrink-0 mt-1 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="border-t border-slate-100">
            <div className="p-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <MethodContextBox icon={Eye} label="What is happening" color="bg-blue-50 border-blue-200 text-blue-700">{section.what}</MethodContextBox>
                <MethodContextBox icon={AlertTriangle} label="Why it matters" color="bg-amber-50 border-amber-200 text-amber-700">{section.why}</MethodContextBox>
                <MethodContextBox icon={Users} label="How it affects you" color="bg-purple-50 border-purple-200 text-purple-700">{section.how_affects_you}</MethodContextBox>
                <MethodContextBox icon={Lightbulb} label="What you can do" color="bg-emerald-50 border-emerald-200 text-emerald-700">{section.what_to_do}</MethodContextBox>
              </div>
              {section.methods && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {section.methods.map((m, i) => (
                    <div key={i} className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                      <p className="text-xs font-bold text-slate-900">{m.name}</p>
                      <p className="text-[11px] text-slate-600 mt-0.5">{m.description}</p>
                    </div>
                  ))}
                </div>
              )}
              {section.biases && (
                <div className="space-y-2">
                  {section.biases.map((b, i) => (
                    <div key={i} className="rounded-xl bg-red-50 border border-red-200 p-3 flex items-start gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-bold text-red-800">{b.name}</p>
                        <p className="text-[11px] text-slate-700 mt-0.5">{b.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {section.terms && (
                <div className="grid sm:grid-cols-2 gap-2">
                  {section.terms.map((t, i) => (
                    <div key={i} className="rounded-xl bg-slate-50 border border-slate-100 p-3 flex items-start gap-2">
                      <ChevronRight className="w-3 h-3 text-teal-500 shrink-0 mt-0.5" />
                      <span className="text-xs text-slate-600"><strong className="text-slate-900">{t.term}:</strong> {t.definition}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const ResearchMethodsSection = () => {
  const [sections, setSections] = useState([]);
  useEffect(() => {
    fetchWithFallback(`/api/education/methods`).then(d => setSections(d.sections || [])).catch(() => {});
  }, []);

  if (sections.length === 0) return null;

  return (
    <section className="space-y-4" data-testid="research-methods-section">
      <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-4 py-1.5 text-sm font-semibold text-indigo-700 mb-3">
          <Microscope className="w-4 h-4" /> Research Methods
        </div>
        <h2 className="text-2xl font-bold text-slate-900">How Health Research Works</h2>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl mx-auto">Study designs, biases, and how to read health research critically</p>
      </motion.div>
      <div className="space-y-3">
        {sections.map((s, i) => <MethodCard key={s.id} section={s} index={i} />)}
      </div>
    </section>
  );
};

// ============ MAIN PAGE COMPONENT ============
export default function PublicHealth101() {
  const [quickStats, setQuickStats] = useState([]);
  const [myths, setMyths] = useState([]);
  const [analogies, setAnalogies] = useState([]);
  const [dataCoverage, setDataCoverage] = useState(null);
  const [topics, setTopics] = useState([]);
  const [stories, setStories] = useState([]);

  useEffect(() => {
    fetchWithFallback(`/api/education/quick-stats`).then(d => setQuickStats(d.stats || [])).catch(() => {});
    fetchWithFallback(`/api/education/myth-buster`).then(d => setMyths(d.myths || [])).catch(() => {});
    fetchWithFallback(`/api/education/analogies`).then(d => setAnalogies(d.analogies || [])).catch(() => {});
    fetchWithFallback(`/api/data-coverage`).then(d => setDataCoverage(d)).catch(() => {});
    fetchWithFallback(`/api/education/topics`).then(d => setTopics(d.topics || [])).catch(() => {});
    fetchWithFallback(`/api/stories`).then(d => setStories(d.stories || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50/50" data-testid="public-health-101-page">
      <PageSEO title="Learn Public Health" description="Free public health education: flashcards, myth busters, landmark studies, glossary, and recommended books. Learn epidemiology, disease prevention, and health statistics." path="/learn" />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSIjZmZmIiBzdHJva2Utd2lkdGg9IjIiLz48L2c+PC9zdmc+')] bg-repeat" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm"
            >
              <Sparkles className="w-4 h-4" />
              Interactive Learning Experience
            </motion.div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
              Learn Public Health
            </h1>
            <p className="text-lg text-violet-100 max-w-2xl mx-auto mb-8">
              Games, visuals & bite-sized facts — no jargon required
            </p>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap justify-center gap-3"
            >
              <a href="#flashcards" className="px-5 py-2.5 rounded-xl bg-white text-violet-700 font-semibold hover:bg-violet-50 transition-colors">
                📚 Study Flashcards
              </a>
              <a href="#myth-buster" className="px-5 py-2.5 rounded-xl bg-white/20 text-white font-semibold hover:bg-white/30 transition-colors backdrop-blur-sm">
                🎮 Myth Buster
              </a>
              <a href="#glossary" className="px-5 py-2.5 rounded-xl bg-white/20 text-white font-semibold hover:bg-white/30 transition-colors backdrop-blur-sm">
                📖 Glossary
              </a>
              <a href="#concepts" className="px-5 py-2.5 rounded-xl bg-white/20 text-white font-semibold hover:bg-white/30 transition-colors backdrop-blur-sm">
                💡 Concepts
              </a>
            </motion.div>
          </motion.div>
        </div>
        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" className="w-full h-16">
            <path d="M0 80V30C240 70 480 10 720 30C960 50 1200 10 1440 30V80H0Z" fill="#f8fafc" />
          </svg>
        </div>
      </section>

      <div className="mx-auto max-w-7xl space-y-12 px-4 py-10 sm:px-6 lg:px-8">
        {/* ========== STICKY TAB NAVIGATION ========== */}
        <nav
          className="sticky top-16 z-30 -mx-4 sm:-mx-6 lg:-mx-8 -mt-10 mb-2 bg-white/95 backdrop-blur-md border-b border-slate-200"
          data-testid="learn-tab-nav"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex gap-1 overflow-x-auto py-2.5 scrollbar-hide">
              {[
                { id: 'myth-buster', label: 'Myth Buster', icon: Gamepad2 },
                { id: 'concepts', label: 'Concepts', icon: Lightbulb },
                { id: 'flashcards', label: 'Flashcards', icon: Layers },
                { id: 'glossary', label: 'Glossary', icon: BookMarked },
                { id: 'landmark-studies', label: 'Studies', icon: FlaskConical },
                { id: 'career-paths', label: 'Careers', icon: Briefcase },
                { id: 'recommended-books', label: 'Books', icon: Book },
              ].map((tab) => (
                <a
                  key={tab.id}
                  href={`#${tab.id}`}
                  data-testid={`learn-tab-${tab.id}`}
                  className="flex items-center gap-1.5 shrink-0 px-3.5 py-1.5 rounded-lg text-sm font-semibold text-slate-600 hover:bg-violet-50 hover:text-violet-700 transition-colors"
                >
                  <tab.icon className="w-3.5 h-3.5" />
                  {tab.label}
                </a>
              ))}
            </div>
          </div>
        </nav>

        {/* Animated Stats */}
        <section data-testid="quick-stats">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-6"
          >
            <h2 className="text-2xl font-bold text-slate-900">The Numbers That Matter</h2>
            <p className="text-slate-500 mt-1">Key stats about public health in America</p>
          </motion.div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {quickStats.map((stat, i) => (
              <AnimatedStatCard key={stat.id} stat={stat} index={i} />
            ))}
          </div>
        </section>

        {/* Myth Buster Game */}
        <section id="myth-buster" className="scroll-mt-20" data-testid="myth-buster-game">
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-violet-600 to-purple-600 p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                  <Gamepad2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Myth Buster</h2>
                  <p className="text-sm text-violet-200">True or false? Test your health knowledge!</p>
                </div>
              </div>
            </div>
            <div className="p-5">
              {myths.length > 0 ? (
                <MythBusterGame myths={myths} />
              ) : (
                <div className="text-center py-8 text-slate-400">Loading game...</div>
              )}
            </div>
          </div>
        </section>

        {/* Visual Analogies */}
        <section id="concepts" className="scroll-mt-20" data-testid="visual-analogies">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-6"
          >
            <h2 className="text-2xl font-bold text-slate-900">Concepts Made Simple</h2>
            <p className="text-slate-500 mt-1">Tap each card to reveal an everyday analogy</p>
          </motion.div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {analogies.map((analogy, i) => (
              <AnalogyCard key={analogy.id} analogy={analogy} index={i} />
            ))}
          </div>
        </section>

        {/* Topic Cards - Condensed */}
        {topics.length > 0 && (
          <section data-testid="topic-cards">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-6"
            >
              <h2 className="text-2xl font-bold text-slate-900">Core Topics</h2>
              <p className="text-slate-500 mt-1">Tap to expand and learn more</p>
            </motion.div>
            <div className="grid gap-3 sm:grid-cols-2">
              {topics.slice(0, 6).map((topic, i) => (
                <motion.details
                  key={topic.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="group rounded-xl bg-white border border-slate-200 overflow-hidden"
                >
                  <summary className="flex items-center gap-3 p-4 cursor-pointer hover:bg-slate-50 transition-colors list-none">
                    <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
                      <BookOpen className="w-4 h-4 text-violet-600" />
                    </div>
                    <span className="font-semibold text-slate-900 flex-1">{topic.title}</span>
                    <ChevronRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="px-4 pb-4 pt-2 border-t border-slate-100">
                    <p className="text-sm text-slate-600 leading-relaxed mb-3">{topic.content}</p>
                    {topic.key_points && (
                      <ul className="space-y-1">
                        {topic.key_points.map((point, j) => (
                          <li key={j} className="flex items-start gap-2 text-xs text-slate-500">
                            <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                            {point}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </motion.details>
              ))}
            </div>
          </section>
        )}

        {/* ========== FLASHCARD STUDY SYSTEM ========== */}
        <section id="flashcards" className="scroll-mt-20" data-testid="flashcards-section">
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                  <Layers className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Flashcard Study System</h2>
                  <p className="text-sm text-emerald-100">Master epidemiology concepts with interactive flashcards</p>
                </div>
              </div>
            </div>
            <div className="p-5">
              <FlashcardsSection />
            </div>
          </div>
        </section>

        {/* ========== GLOSSARY ========== */}
        <section id="glossary" className="scroll-mt-20" data-testid="glossary-section">
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                  <BookMarked className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Epidemiology Glossary</h2>
                  <p className="text-sm text-blue-100">Searchable definitions of key terms</p>
                </div>
              </div>
            </div>
            <div className="p-5">
              <GlossarySection />
            </div>
          </div>
        </section>

        {/* ========== LANDMARK STUDIES ========== */}
        <LandmarkStudiesSection />

        {/* ========== PUBLIC HEALTH CAREER PATHS (for students) ========== */}
        <CareerPathsSection />

        {/* ========== RECOMMENDED BOOKS ========== */}
        <RecommendedBooksSection />

        {/* ========== RESEARCH METHODS SECTION (embedded) ========== */}
        <ResearchMethodsSection />

        {/* Footer Note */}
        <p className="text-center text-xs text-slate-400">
          Data: CDC PLACES 2025, WHO, NIH · For educational purposes
        </p>
      </div>

      {/* CSS for 3D transforms */}
      <style>{`
        .perspective-1000 { perspective: 1000px; }
        .preserve-3d { transform-style: preserve-3d; }
        .backface-hidden { backface-visibility: hidden; }
        .rotate-y-180 { transform: rotateY(180deg); }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
